<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const showAlert = useAlert;

const field = ref('phone_number');
const groups = ref([]);
const total = ref(0);
const page = ref(1);
const isLoading = ref(false);
const mergingGroupIndex = ref(null);

// Which contact is selected as primary per group (index in group.contacts)
const primaryIndex = ref({});

const hasMore = computed(() => groups.value.length < total.value);

const fieldLabel = computed(() =>
  field.value === 'phone_number' ? 'Telefone' : 'E-mail'
);

async function fetchDuplicates(reset = false) {
  if (reset) {
    groups.value = [];
    page.value = 1;
    primaryIndex.value = {};
  }
  isLoading.value = true;
  try {
    const res = await ContactAPI.getDuplicates(field.value, page.value);
    const data = res.data;
    total.value = data.total;
    // Initialize primary selection to index 0 (oldest) for new groups
    data.payload.forEach((g, i) => {
      const key = `${page.value}-${i}`;
      if (primaryIndex.value[key] === undefined) primaryIndex.value[key] = 0;
    });
    groups.value = [...groups.value, ...data.payload];
  } catch {
    showAlert('Erro ao carregar duplicatas');
  } finally {
    isLoading.value = false;
  }
}

async function loadMore() {
  page.value += 1;
  await fetchDuplicates();
}

async function mergeGroup(groupIdx) {
  const group = groups.value[groupIdx];
  const pageOffset = Math.floor(groupIdx / 20);
  const localIdx = groupIdx % 20;
  const key = `${pageOffset + 1}-${localIdx}`;
  const primary = group.contacts[primaryIndex.value[key] ?? 0];
  const others = group.contacts.filter(c => c.id !== primary.id);

  mergingGroupIndex.value = groupIdx;
  try {
    for (const secondary of others) {
      await store.dispatch('contacts/merge', {
        parentId: primary.id,
        childId: secondary.id,
      });
    }
    showAlert(`Mesclados ${others.length + 1} contatos em "${primary.name}"`);
    groups.value.splice(groupIdx, 1);
    total.value -= 1;
  } catch {
    showAlert('Erro ao mesclar contatos');
  } finally {
    mergingGroupIndex.value = null;
  }
}

function onFieldChange() {
  fetchDuplicates(true);
}

function goBack() {
  router.push({ name: 'contacts_dashboard_index' });
}

onMounted(() => fetchDuplicates(true));
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto bg-n-background">
    <!-- Header -->
    <div class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div
        class="flex items-center gap-3 px-6 py-4 max-w-[60rem] mx-auto w-full"
      >
        <button
          class="flex items-center justify-center size-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11"
          @click="goBack"
        >
          <i class="i-lucide-arrow-left size-4" />
        </button>
        <div class="flex-1">
          <h1 class="text-xl font-medium text-n-slate-12">
            Contatos Duplicados
          </h1>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ total }} grupo{{ total !== 1 ? 's' : '' }} de duplicatas por
            {{ fieldLabel }}
          </p>
        </div>
        <!-- Field selector -->
        <div class="flex items-center gap-1 bg-n-alpha-2 rounded-lg p-1">
          <button
            class="px-3 py-1 rounded-md text-sm font-medium transition-colors"
            :class="
              field === 'phone_number'
                ? 'bg-white dark:bg-n-solid-2 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="
              field = 'phone_number';
              onFieldChange();
            "
          >
            Telefone
          </button>
          <button
            class="px-3 py-1 rounded-md text-sm font-medium transition-colors"
            :class="
              field === 'email'
                ? 'bg-white dark:bg-n-solid-2 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="
              field = 'email';
              onFieldChange();
            "
          >
            E-mail
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 px-6 py-6 max-w-[60rem] mx-auto w-full">
      <!-- Loading skeleton -->
      <div v-if="isLoading && groups.length === 0" class="space-y-4">
        <div
          v-for="n in 4"
          :key="n"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 animate-pulse"
        >
          <div class="h-4 w-32 bg-n-alpha-2 rounded mb-3" />
          <div class="grid grid-cols-2 gap-3">
            <div class="h-20 bg-n-alpha-2 rounded-lg" />
            <div class="h-20 bg-n-alpha-2 rounded-lg" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!isLoading && groups.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <i class="i-lucide-check-circle size-12 text-emerald-500 mb-4" />
        <p class="text-base font-medium text-n-slate-12">
          Nenhuma duplicata encontrada
        </p>
        <p class="text-sm text-n-slate-11 mt-1">
          Todos os contatos por {{ fieldLabel }} são únicos.
        </p>
      </div>

      <!-- Groups list -->
      <div v-else class="space-y-4">
        <div
          v-for="(group, groupIdx) in groups"
          :key="`${group.field}-${group.value}-${groupIdx}`"
          class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <!-- Group header -->
          <div
            class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center gap-2">
              <i
                :class="
                  group.field === 'phone_number'
                    ? 'i-lucide-phone'
                    : 'i-lucide-mail'
                "
                class="size-4 text-n-slate-11"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                group.value
              }}</span>
              <span
                class="text-xs text-n-slate-11 bg-n-alpha-2 px-2 py-0.5 rounded-full"
              >
                {{ group.contacts.length }} contatos
              </span>
            </div>
            <button
              class="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
              :disabled="mergingGroupIndex === groupIdx"
              @click="mergeGroup(groupIdx)"
            >
              <i
                :class="
                  mergingGroupIndex === groupIdx
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-git-merge'
                "
                class="size-3.5"
              />
              {{ mergingGroupIndex === groupIdx ? 'Mesclando...' : 'Mesclar' }}
            </button>
          </div>

          <!-- Contacts grid -->
          <div class="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label
              v-for="(contact, cIdx) in group.contacts"
              :key="contact.id"
              class="relative flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
              :class="
                (primaryIndex[
                  `${Math.floor(groupIdx / 20) + 1}-${groupIdx % 20}`
                ] ?? 0) === cIdx
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak hover:border-n-slate-6 hover:bg-n-alpha-1'
              "
            >
              <input
                type="radio"
                :name="`group-${groupIdx}`"
                class="mt-0.5 accent-n-brand"
                :checked="
                  (primaryIndex[
                    `${Math.floor(groupIdx / 20) + 1}-${groupIdx % 20}`
                  ] ?? 0) === cIdx
                "
                @change="
                  primaryIndex[
                    `${Math.floor(groupIdx / 20) + 1}-${groupIdx % 20}`
                  ] = cIdx
                "
              />
              <!-- Avatar -->
              <div class="shrink-0">
                <img
                  v-if="contact.thumbnail"
                  :src="contact.thumbnail"
                  class="size-9 rounded-full object-cover"
                />
                <div
                  v-else
                  class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center text-sm font-medium text-n-slate-11"
                >
                  {{ (contact.name || '?')[0].toUpperCase() }}
                </div>
              </div>
              <!-- Info -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ contact.name || '(sem nome)' }}
                </p>
                <p
                  v-if="contact.email"
                  class="text-xs text-n-slate-11 truncate"
                >
                  {{ contact.email }}
                </p>
                <p
                  v-if="contact.phone_number"
                  class="text-xs text-n-slate-11 truncate"
                >
                  {{ contact.phone_number }}
                </p>
                <p class="text-[10px] text-n-slate-9 mt-0.5">
                  Criado
                  {{ new Date(contact.created_at).toLocaleDateString('pt-BR') }}
                </p>
              </div>
              <!-- Primary badge -->
              <span
                v-if="
                  (primaryIndex[
                    `${Math.floor(groupIdx / 20) + 1}-${groupIdx % 20}`
                  ] ?? 0) === cIdx
                "
                class="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wide text-n-brand bg-n-brand/10 px-1.5 py-0.5 rounded"
              >
                Manter
              </span>
            </label>
          </div>

          <p class="px-4 pb-3 text-[11px] text-n-slate-10">
            Selecione qual contato manter. Os demais serão mesclados nele.
          </p>
        </div>

        <!-- Load more -->
        <div v-if="hasMore" class="flex justify-center pt-2 pb-6">
          <button
            class="flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12 px-4 py-2 rounded-lg hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
            :disabled="isLoading"
            @click="loadMore"
          >
            <i
              :class="
                isLoading
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-chevron-down'
              "
              class="size-4"
            />
            {{
              isLoading
                ? 'Carregando...'
                : `Carregar mais (${total - groups.length} restantes)`
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
